import React from 'react';
import { AlertCircle, CheckCircle, Loader, StopCircle, WifiOff, Clock } from 'lucide-react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Countdown timer component
const CountdownTimer: React.FC<{ startTime: string }> = ({ startTime }) => {
  const [timeLeft, setTimeLeft] = React.useState<number>(0);

  React.useEffect(() => {
    const calculateTimeLeft = () => {
      const start = new Date(startTime).getTime();
      const end = start + (2 * 60 * 1000); // 2 minutes
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((end - now) / 1000));
      return remaining;
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const percentage = ((120 - timeLeft) / 120) * 100;

  return (
    <div className="flex items-center gap-2 text-blue-400">
      <Clock className="w-3.5 h-3.5" />
      <span className="text-xs font-medium">
        Waiting for DKIM: {minutes}:{seconds.toString().padStart(2, '0')}
      </span>
      <div className="flex-1 bg-gray-800 rounded-full h-1 overflow-hidden ml-2">
        <div
          className="h-full bg-blue-500 transition-all duration-1000"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

interface Workflow {
  id: string;
  customer_id: string;
  status: string;
  current_step: string;
  total_accounts: number;
  processed_accounts: number;
  total_work_units: number;
  completed_work_units: number;
}

const ProgressDisplay: React.FC = () => {
  const { progressData, jobStatus, isConnected, lastMessage } = useWebSocket();
  const queryClient = useQueryClient();

  // Track DKIM wait periods per workflow
  const [dkimWaitTimes, setDkimWaitTimes] = React.useState<Record<string, string>>({});

  // Listen for DKIM wait events
  React.useEffect(() => {
    if (lastMessage) {
      // Only invalidate on specific workflow-related events that have workflow_id
      if ((lastMessage.type === 'dkim_wait_started' ||
           lastMessage.type === 'dkim_setup_started' ||
           lastMessage.type === 'job_completed') &&
          lastMessage.workflow_id) {
        // Refetch workflows immediately when workflow state changes
        queryClient.refetchQueries({ queryKey: ['workflows'] });
      }

      if (lastMessage.type === 'dkim_wait_started' && lastMessage.workflow_id) {
        const startTime = lastMessage.start_time || new Date().toISOString();
        setDkimWaitTimes(prev => ({
          ...prev,
          [lastMessage.workflow_id]: startTime,
        }));
        // Clear after 2 minutes
        setTimeout(() => {
          setDkimWaitTimes(prev => {
            const newTimes = { ...prev };
            delete newTimes[lastMessage.workflow_id];
            return newTimes;
          });
        }, 120000);
      }
      // Clear countdown if DKIM started or job completed
      else if ((lastMessage.type === 'dkim_setup_started' ||
                lastMessage.type === 'job_completed') &&
               lastMessage.workflow_id) {
        setDkimWaitTimes(prev => {
          const newTimes = { ...prev };
          delete newTimes[lastMessage.workflow_id];
          return newTimes;
        });
      }
    }
  }, [lastMessage, queryClient]);

  // Fetch all workflows to aggregate progress for active ones
  const { data: workflows } = useQuery({
    queryKey: ['workflows'],
    queryFn: api.getWorkflows,
    refetchInterval: 3000,
    structuralSharing: false, // Prevent React Query from keeping stale data in cache
  });

  // Get all active workflows (PROCESSING status)
  const activeWorkflows = workflows?.filter((w: Workflow) => w.status === 'PROCESSING') || [];

  // Debug log to see what data we're getting
  React.useEffect(() => {
    if (activeWorkflows.length > 0) {
      console.log('Active workflows:', activeWorkflows.map((w: any) => ({
        id: w.id.substring(0, 8),
        customer: w.customer_id,
        processed: w.processed_accounts,
        total: w.total_accounts,
        updated: w.updated_at
      })));
    }
  }, [activeWorkflows]);

  const getStatusIcon = () => {
    if (!jobStatus) return null;
    
    switch (jobStatus.status) {
      case 'started':
      case 'running':
        return <Loader className="w-5 h-5 text-orange-400 animate-spin" />;
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'stopped':
        return <StopCircle className="w-5 h-5 text-red-400" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    if (!jobStatus) return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    
    switch (jobStatus.status) {
      case 'started':
      case 'running':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'complete':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'stopped':
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getProgressType = () => {
    if (!progressData) return 'Processing';

    // Check if we have job mode from jobStatus (for domain-related jobs)
    if (jobStatus?.jobMode) {
      switch (jobStatus.jobMode) {
        case 'dkim':
          return 'DKIM Configuration';
        case 'setup':
          return 'Domain Setup';
        case 'imap':
          return 'IMAP Configuration';
        case 'user':
          return 'User Creation';
        default:
          // Fall through to check progressData.type
          break;
      }
    }

    switch (progressData.type) {
      case 'domain':
        return 'Domain Processing';
      case 'imap':
        return 'IMAP Configuration';
      case 'user':
        return 'User Creation';
      default:
        return 'Processing';
    }
  };

  // Show nothing if no active workflows and no WebSocket data
  if (activeWorkflows.length === 0 && !progressData && !jobStatus) {
    return null;
  }

  return (
    <div className="dashboard-card">
      <div className="space-y-4">
        {!isConnected && (
          <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <WifiOff className="w-5 h-5 text-yellow-400" />
            <div>
              <div className="text-yellow-400 font-medium">WebSocket Disconnected</div>
              <div className="text-sm text-yellow-400/70">Real-time updates unavailable. Reconnecting...</div>
            </div>
          </div>
        )}

        {/* Show individual workflow progress */}
        {activeWorkflows.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-700/50">
              <Loader className="w-5 h-5 text-orange-400 animate-spin" />
              <span className="text-white font-medium">Active Workflows:</span>
              <span className="px-2 py-1 rounded-full text-xs font-medium border bg-orange-500/20 text-orange-400 border-orange-500/30">
                {activeWorkflows.length} RUNNING
              </span>
            </div>

            <div className="space-y-3">
              {activeWorkflows.slice(0, 5).map((workflow: Workflow) => {
                const percentage = workflow.total_accounts > 0
                  ? (workflow.processed_accounts / workflow.total_accounts) * 100
                  : 0;
                const isQueued = workflow.status === 'QUEUED';
                const isDkimWaiting = dkimWaitTimes[workflow.id];

                return (
                  <div
                    key={`${workflow.id}-${workflow.processed_accounts}-${workflow.total_accounts}`}
                    className="space-y-2 p-3 bg-gray-900/30 rounded-lg border border-gray-700/30"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-orange-400 font-mono text-sm font-medium">
                          {workflow.customer_id}
                        </span>
                        {isQueued && (
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            QUEUED
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {workflow.id.substring(0, 8)}
                      </span>
                    </div>

                    <div className="text-sm text-gray-300">
                      {workflow.current_step}
                    </div>

                    {/* Show DKIM countdown if this workflow is waiting */}
                    {isDkimWaiting && (
                      <CountdownTimer startTime={isDkimWaiting} />
                    )}

                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Progress</span>
                        <span>{workflow.processed_accounts}/{workflow.total_accounts} ({Math.round(percentage)}%)</span>
                      </div>
                      <div className="relative w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            isQueued
                              ? 'bg-blue-500'
                              : 'bg-gradient-to-r from-orange-500 to-yellow-500'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {activeWorkflows.length > 5 && (
                <div className="text-center text-sm text-gray-500 pt-2">
                  + {activeWorkflows.length - 5} more workflows running...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fallback to WebSocket data if no active workflows but WebSocket has data */}
        {activeWorkflows.length === 0 && jobStatus && (
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">Job Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
                  {jobStatus.status.toUpperCase()}
                </span>
              </div>
              {jobStatus.message && (
                <div className={`text-sm mt-1 ${jobStatus.status === 'failed' ? 'text-red-400' : 'text-gray-400'}`}>
                  {jobStatus.message}
                </div>
              )}
            </div>
          </div>
        )}

        {activeWorkflows.length === 0 && progressData && (
          <div className="space-y-3">
            <div className="text-white font-medium">{getProgressType()} Progress</div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">
                  {progressData.current ? `Processing: ${progressData.current}` : 'Processing...'}
                </span>
                <span className="text-gray-400">
                  {progressData.processed}/{progressData.total} ({Math.round(progressData.percentage || 0)}%)
                </span>
              </div>

              <div className="relative w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ease-out ${
                    jobStatus?.status === 'failed'
                      ? 'bg-gradient-to-r from-red-500 to-red-600'
                      : 'bg-gradient-to-r from-orange-500 to-yellow-500'
                  }`}
                  style={{ width: `${Math.min(progressData.percentage || 0, 100)}%` }}
                />
                {jobStatus?.status === 'running' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                )}
              </div>
            </div>

            {progressData.failed !== undefined && progressData.failed > 0 && (
              <div className="flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span>Failed items: {progressData.failed}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressDisplay;